import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { LanguageProvider } from '@/context/LanguageContext';
import { FullScreenLoader } from '@/components/FullScreenLoader';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RouteTracker } from '@/components/RouteTracker';

import Onboarding from '@/pages/Onboarding';
import Auth from '@/pages/Auth';
import Dashboard from '@/pages/Dashboard';
import Goals from '@/pages/Goals';
import Tasks from '@/pages/Tasks';
import Projects from '@/pages/Projects';
import ProjectDetail from '@/pages/ProjectDetail';
import Finance from '@/pages/Finance';
import Habits from '@/pages/Habits';
import AIAssistant from '@/pages/AIAssistant';
import DailyReview from '@/pages/DailyReview';
import Settings from '@/pages/Settings';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import DeleteAccount from '@/pages/DeleteAccount';
import AiNotice from '@/pages/AiNotice';

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <RouteTracker />
            <Routes>
              <Route path="/" element={<PublicOnly><Onboarding /></PublicOnly>} />
              <Route path="/auth" element={<PublicOnly><Auth /></PublicOnly>} />
              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="/goals" element={<Protected><Goals /></Protected>} />
              <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
              <Route path="/projects" element={<Protected><Projects /></Protected>} />
              <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
              <Route path="/finance" element={<Protected><Finance /></Protected>} />
              <Route path="/habits" element={<Protected><Habits /></Protected>} />
              <Route path="/ai" element={<Protected><AIAssistant /></Protected>} />
              <Route path="/review" element={<Protected><DailyReview /></Protected>} />
              <Route path="/settings" element={<Protected><Settings /></Protected>} />

              {/* Public, unauthenticated legal pages (no redirect to /dashboard) */}
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/delete-account" element={<DeleteAccount />} />
              <Route path="/ai-notice" element={<AiNotice />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </ErrorBoundary>
        <Toaster position="top-center" richColors theme="dark" />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
